import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge, Textarea, Skeleton } from "@/components/ui-kit";
import { Plus, Trash2, Pencil, AlertTriangle, Building2, Landmark, PenLine, Tags, Ruler, Users, Upload, History, Mail, MessageSquareText, UserRound, FolderTree, Bot, Percent, Clock, Send, ScanFace } from "lucide-react";
import { EmailReportSettings } from "@/components/settings/EmailReportSettings";
import Karyawan from "@/pages/Karyawan";

const TABS = [
  { key: "companies", label: "Profil Perusahaan", icon: Building2 },
  { key: "banks", label: "Rekening Bank", icon: Landmark },
  { key: "clients", label: "Klien", icon: Users },
  { key: "karyawan", label: "Karyawan", icon: UserRound },
  { key: "department_categories", label: "Kategori Departemen", icon: FolderTree },
  { key: "signatories", label: "Penanda Tangan", icon: PenLine },
  { key: "categories", label: "Kategori", icon: Tags },
  // Iter v34bf — Kategori Pengajuan Fasilitas / Alat / Keperluan.
  { key: "request_categories", label: "Kategori Pengajuan", icon: FolderTree },
  { key: "units", label: "Satuan", icon: Ruler },
  { key: "sj_codes", label: "Kode SJ", icon: Tags },
  { key: "quote_notes", label: "Template Catatan Penawaran", icon: MessageSquareText },
  { key: "input_history", label: "Riwayat Input", icon: History },
  { key: "email_report", label: "Email Stok Harian", icon: Mail },
  { key: "payment_reminder", label: "Pengingat Pembayaran", icon: Mail },
  // Iter v34{ab} — Konfigurasi email pengingat tagihan jatuh tempo (Daftar Tagihan).
  { key: "overdue_reminder", label: "Email Reminder Jatuh Tempo", icon: Mail },
  // Iter v34bq — Email pengingat PO belum di-supply / belum ditagih.
  { key: "po_reminders", label: "Email Reminder PO (Supply & Tagih)", icon: Mail },
  // Iter v34br (Task 4) — Template Pengumuman untuk pemakaian berulang.
  { key: "announcement_templates", label: "Template Pengumuman", icon: MessageSquareText },
  // Iter v34z (#4) — Email body customization per reminder type
  { key: "email_templates", label: "Template Email (Custom)", icon: Mail },
  // Iter v34z (#8 + #9) — Format Nomor Dokumen & Alert Stok Minim
  { key: "doc_number_formats", label: "Format Nomor PO (Custom)", icon: Tags },
  { key: "ppn_defaults", label: "PPN Default Tagihan", icon: Percent },  // Iter 64 (#3)
  { key: "overtime_default", label: "Default Tarif Lembur", icon: Clock },  // Iter v34ay
  { key: "wa_marketing", label: "WhatsApp Marketing", icon: MessageSquareText },  // Iter v34b0
  { key: "stock_alerts", label: "Alert Stok Minim (Batching)", icon: AlertTriangle },
  { key: "ai_assistant", label: "AI Assistant (CS & HR)", icon: Bot },
  // Iter v34bw (#1) — Validasi foto KTP & bank karyawan
  { key: "employee_registration", label: "Validasi Pendaftaran Karyawan", icon: Users },
  // Iter v34ci — PIN Kiosk Absen (Face)
  { key: "kiosk_pin", label: "PIN Kiosk Absen", icon: ScanFace },
  { key: "reset", label: "Reset Data", icon: AlertTriangle },
];

export default function Settings() {
  const { t } = useLang();
  const [tab, setTab] = useState(null); // Iter v28 #6 — null until user clicks a list item

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader overline="Admin · Konfigurasi" title={t("settings")}
        description="Master data sistem: pilih kategori dari daftar di kiri untuk membuka pengaturannya."
      />

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* Settings list — Iter v28 #6 */}
        <Card className="p-0 self-start sticky top-4 overflow-hidden">
          <div className="p-3 border-b border-stone-200 bg-stone-50/60">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-stone-500">Pengaturan</div>
          </div>
          <ul className="divide-y divide-stone-100" data-testid="settings-list">
            {TABS.map((x) => (
              <li key={x.key}>
                <button
                  onClick={() => setTab(tab === x.key ? null : x.key)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between gap-2 transition-colors ${
                    tab === x.key ? "bg-blue-50 text-blue-800 border-l-2 border-blue-500" : "text-stone-700 hover:bg-stone-50"
                  }`}
                  data-testid={`settings-tab-${x.key}`}
                >
                  <span className="flex items-center gap-3 text-sm">
                    <x.icon className={`h-4 w-4 ${tab === x.key ? "text-terra-600" : "text-stone-500"}`} />
                    {x.label}
                  </span>
                  <span className={`text-stone-400 transition-transform ${tab === x.key ? "rotate-90" : ""}`}>›</span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div className="min-h-[200px]" data-testid="settings-panel">
          {!tab && (
            <Card className="p-12 text-center">
              <div className="text-stone-400 text-sm">Pilih kategori pengaturan dari daftar di sebelah kiri untuk mulai.</div>
            </Card>
          )}
          {tab === "companies" && <CompanyProfiles />}
          {tab === "banks" && <Banks />}
          {tab === "clients" && <Clients />}
          {tab === "karyawan" && <Karyawan />}
          {tab === "department_categories" && <DepartmentCategories />}
          {tab === "signatories" && <Signatories />}
      {tab === "categories" && <MasterList kind="categories" label="Kategori" />}
      {tab === "request_categories" && <RequestCategoriesSettings />}
      {tab === "units" && <MasterList kind="units" label="Satuan" />}
      {tab === "sj_codes" && <MasterList kind="sj_codes" label="Kode SJ" />}
      {tab === "quote_notes" && <QuoteNoteTemplates />}
      {tab === "input_history" && <InputHistoryManager />}
      {tab === "email_report" && <EmailReportSettings />}
      {tab === "payment_reminder" && <PaymentReminderSettings />}
      {tab === "overdue_reminder" && <OverdueReminderSettings />}
      {tab === "po_reminders" && <PoReminderSettings />}
      {tab === "announcement_templates" && <AnnouncementTemplateSettings />}
      {tab === "email_templates" && <EmailTemplateSettings />}
      {tab === "doc_number_formats" && <DocNumberFormatSettings />}
      {tab === "ppn_defaults" && <PpnDefaultSettings />}
      {tab === "overtime_default" && <OvertimeDefaultSettings />}
      {tab === "wa_marketing" && <WhatsAppMarketingSettings />}
      {tab === "stock_alerts" && <StockAlertSettings />}
      {tab === "ai_assistant" && <AIAssistantSettings />}
      {tab === "employee_registration" && <EmployeeRegistrationSettings />}
      {tab === "kiosk_pin" && <KioskPinSettings />}
      {tab === "reset" && <ResetData />}
        </div>
      </div>
    </div>
    </div>
  );
}

// Iter 64 (#3) — PPN Default Settings — global toggle + rate for new invoices.
function PpnDefaultSettings() {
  const [s, setS] = useState({ default_vat_enabled: true, default_vat_rate: 11.0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings")
      .then((r) => setS({
        default_vat_enabled: r.data.default_vat_enabled !== false,
        default_vat_rate: r.data.default_vat_rate ?? 11.0,
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        default_vat_enabled: !!s.default_vat_enabled,
        default_vat_rate: Number(s.default_vat_rate) || 0,
      });
      toast.success("Pengaturan PPN tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  if (loading) return <Card className="p-6 text-stone-400 text-sm">Memuat...</Card>;
  return (
    <Card className="p-6 space-y-4" data-testid="ppn-default-settings">
      <div>
        <h3 className="text-lg font-semibold text-stone-800">PPN Default Tagihan</h3>
        <p className="text-xs text-stone-500 mt-1">
          Mengontrol nilai default <b>Gunakan PPN</b> + <b>Tarif</b> saat operator membuat tagihan baru.
          Jika invoice yang dibuat dari PO menggunakan PPN sementara PO sumber tidak punya PPN,
          total PO akan otomatis di-recompute dengan PPN agar nilai piutang & procurement tetap sinkron.
        </p>
      </div>
      <label className="flex items-center gap-3 cursor-pointer p-3 bg-stone-50 border border-stone-200 rounded-sm">
        <input
          type="checkbox"
          checked={!!s.default_vat_enabled}
          onChange={(e) => setS({ ...s, default_vat_enabled: e.target.checked })}
          data-testid="ppn-default-enabled"
        />
        <span className="text-sm text-stone-800">Aktifkan PPN sebagai default tagihan baru</span>
      </label>
      <Field label="Tarif PPN (%)" hint="Tarif yang digunakan saat PPN diaktifkan. Standar saat ini: 11%.">
        <Input
          type="number" min="0" max="100" step="0.5"
          value={s.default_vat_rate}
          onChange={(e) => setS({ ...s, default_vat_rate: e.target.value })}
          data-testid="ppn-default-rate"
        />
      </Field>
      <div className="flex justify-end pt-2 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="ppn-default-save">
          {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </Card>
  );
}


// Iter v34ay — Default Tarif Overtime (Rp/jam) fallback ketika per-karyawan 0.
function OvertimeDefaultSettings() {
  const [rate, setRate] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setRate(Number(r.data.default_overtime_hourly_rate) || 0))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { default_overtime_hourly_rate: Math.max(0, Number(rate) || 0) });
      toast.success("Tarif default overtime tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  if (loading) return <Card className="p-6 text-stone-400 text-sm">Memuat...</Card>;
  return (
    <Card className="p-6 space-y-4" data-testid="overtime-default-settings">
      <div>
        <h3 className="text-lg font-semibold text-stone-800">Default Tarif Lembur (Rp/jam)</h3>
        <p className="text-xs text-stone-500 mt-1">
          Tarif upah lembur yang digunakan untuk semua karyawan yang <b>belum diset tarif per-karyawannya</b> (masih 0).
          Ubah via halaman <b>Master Tarif Overtime</b> untuk mengatur tarif per karyawan.
        </p>
      </div>
      <Field label="Tarif Default (Rp per jam lembur)">
        <Input
          type="number" min="0" step="1000"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="font-mono-num"
          placeholder="mis: 25000"
          data-testid="overtime-default-rate"
        />
      </Field>
      <div className="flex justify-end pt-2 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="overtime-default-save">
          {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </Card>
  );
}

// Iter v34b0 — WhatsApp Marketing (nomor untuk tombol "Hubungi Marketing" di Cart Klien).
function WhatsAppMarketingSettings() {
  const [wa, setWa] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setWa(r.data.whatsapp_marketing || ""))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      // Iter v34b3 (fix) — Normalize: strip semua non-digit; jika diawali 0, ganti dengan 62.
      let raw = (wa || "").replace(/[^\d]/g, "");
      if (raw.startsWith("0")) raw = "62" + raw.slice(1);
      // Handle 8xxxx (tanpa 0 di depan)
      if (raw.startsWith("8")) raw = "62" + raw;
      if (raw && !raw.startsWith("62")) raw = "62" + raw;
      if (raw && raw.length < 10) {
        toast.error("Nomor WhatsApp terlalu pendek. Minimal 10 digit.");
        setSaving(false);
        return;
      }
      await api.patch("/settings", { whatsapp_marketing: raw });
      toast.success(`Nomor WhatsApp tersimpan: ${raw}`);
      setWa(raw);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  if (loading) return <Card className="p-6 text-stone-400 text-sm">Memuat...</Card>;
  return (
    <div className="space-y-4">
    <Card className="p-6 space-y-4" data-testid="wa-marketing-settings">
      <div>
        <h3 className="text-lg font-semibold text-stone-800">Nomor WhatsApp Marketing</h3>
        <p className="text-xs text-stone-500 mt-1">
          Nomor WhatsApp ini akan dipanggil ketika klien menekan tombol <b>&quot;Hubungi Marketing&quot;</b> di halaman Keranjang.
          Sertakan kode negara (contoh: <code className="bg-stone-100 px-1">628123456789</code> untuk Indonesia — tanpa tanda + di awal, spasi, atau strip).
        </p>
      </div>
      <Field label="Nomor WhatsApp (format internasional)">
        <Input
          value={wa}
          onChange={(e) => setWa(e.target.value)}
          placeholder="mis: 628123456789"
          className="font-mono-num"
          data-testid="wa-marketing-input"
        />
      </Field>
      <div className="flex justify-end pt-2 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="wa-marketing-save">
          {saving ? "Menyimpan..." : "Simpan"}
        </Button>
      </div>
    </Card>
    <WhatsAppTemplateSettings />
    </div>
  );
}

// Iter v34b6 — Template pesan WhatsApp (dapat diedit oleh super admin).
function WhatsAppTemplateSettings() {
  const [tpl, setTpl] = useState("");
  const [defaultTpl, setDefaultTpl] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => {
      setTpl(r.data.wa_marketing_template || "");
      setDefaultTpl(r.data.wa_marketing_template || "");
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { wa_marketing_template: tpl });
      toast.success("Template pesan WhatsApp tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  const resetDefault = () => {
    const DEFAULT = `*Permintaan Penawaran*
Kepada: {company_name}
Dari: {client_name}
Tanggal: {date}

*Rincian Pesanan:*
{items}

*GRAND TOTAL: {grand_total}*

Mohon konfirmasi ketersediaan & jadwal pengirimannya. Terima kasih.`;
    setTpl(DEFAULT);
  };
  if (loading) return null;
  return (
    <Card className="p-6 space-y-4" data-testid="wa-template-settings">
      <div>
        <h3 className="text-lg font-semibold text-stone-800">Template Pesan &quot;Hubungi Marketing&quot;</h3>
        <p className="text-xs text-stone-500 mt-1">
          Atur teks pesan WhatsApp yang otomatis terisi ketika klien menekan tombol <b>&quot;Hubungi Marketing&quot;</b> di keranjang.
          Gunakan placeholder berikut (akan diganti otomatis):
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
          {[
            { p: "{client_name}", desc: "Nama klien" },
            { p: "{company_name}", desc: "Nama perusahaan Anda" },
            { p: "{date}", desc: "Tanggal hari ini (id-ID)" },
            { p: "{items}", desc: "Blok rincian pesanan (auto)" },
            { p: "{grand_total}", desc: "Grand total (Rp)" },
          ].map((x) => (
            <span key={x.p} title={x.desc} className="inline-flex items-center gap-1 border border-stone-200 bg-stone-50 px-1.5 py-0.5 rounded-sm">
              <code className="font-mono text-blue-700">{x.p}</code>
              <span className="text-stone-500">— {x.desc}</span>
            </span>
          ))}
        </div>
      </div>
      <Field label="Template Pesan">
        <Textarea
          value={tpl}
          onChange={(e) => setTpl(e.target.value)}
          rows={14}
          className="font-mono text-xs whitespace-pre"
          data-testid="wa-template-input"
        />
      </Field>
      <div className="flex justify-between pt-2 border-t border-stone-200">
        <Button variant="ghost" onClick={resetDefault} data-testid="wa-template-reset">Reset Default</Button>
        <Button onClick={save} disabled={saving} data-testid="wa-template-save">
          {saving ? "Menyimpan..." : "Simpan Template"}
        </Button>
      </div>
    </Card>
  );
}



// Iter v34s (#4) — Pengingat Pembayaran (Invoice & Vendor Expense) Settings
function PaymentReminderSettings() {
  const [s, setS] = useState({
    invoice_reminder_enabled: false, invoice_reminder_recipients: [],
    expense_reminder_enabled: false, expense_reminder_recipients: [],
    reminder_time: "08:00", reminder_days_ahead: 3,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    api.get("/payment-reminder-settings").then((r) => setS(r.data)).finally(() => setLoading(false));
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/payment-reminder-settings", {
        invoice_reminder_enabled: !!s.invoice_reminder_enabled,
        invoice_reminder_recipients: (s.invoice_reminder_recipients || []).filter((e) => (e || "").trim()),
        invoice_reminder_to_client_enabled: !!s.invoice_reminder_to_client_enabled,
        expense_reminder_enabled: !!s.expense_reminder_enabled,
        expense_reminder_recipients: (s.expense_reminder_recipients || []).filter((e) => (e || "").trim()),
        reminder_time: s.reminder_time || "08:00",
        reminder_days_ahead: Number(s.reminder_days_ahead) || 3,
        po_upload_email_enabled: !!s.po_upload_email_enabled,
        po_upload_email_recipients: (s.po_upload_email_recipients || []).filter((e) => (e || "").trim()),
        sj_upload_email_enabled: !!s.sj_upload_email_enabled,
        sj_upload_email_recipients: (s.sj_upload_email_recipients || []).filter((e) => (e || "").trim()),
        leave_request_email_enabled: !!s.leave_request_email_enabled,
        leave_request_email_recipients: (s.leave_request_email_recipients || []).filter((e) => (e || "").trim()),
        // Iter v34cr — Peringatan plafon kredit klien
        credit_limit_alert_enabled: !!s.credit_limit_alert_enabled,
        credit_limit_alert_recipients: (s.credit_limit_alert_recipients || []).filter((e) => (e || "").trim()),
        credit_limit_alert_include_marketing_roles: !!s.credit_limit_alert_include_marketing_roles,
        credit_limit_alert_to_client_enabled: !!s.credit_limit_alert_to_client_enabled,
        credit_limit_alert_threshold_pct: Number(s.credit_limit_alert_threshold_pct) || 80,
        credit_limit_alert_time: s.credit_limit_alert_time || "08:30",
        credit_limit_alert_cooldown_days: Number(s.credit_limit_alert_cooldown_days) || 7,
      });
      toast.success("Pengaturan pengingat tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    setSaving(false);
  };
  const sendTest = async (type = null) => {
    setTesting(true);
    try {
      const url = type ? `/payment-reminder-settings/test?type=${type}` : "/payment-reminder-settings/test";
      const r = await api.post(url);
      const data = r.data || {};
      const results = data.results || {};
      const labels = {
        invoice_digest: "Pengingat Invoice (Digest)",
        invoice_client: "Pengingat Invoice (Per-Klien)",
        expense_digest: "Pengingat Tagihan Vendor",
        po_upload: "Notifikasi PO Baru",
        sj_upload: "Notifikasi Surat Jalan Baru",
      };
      const lines = Object.entries(results).map(([k, v]) => {
        const lbl = labels[k] || k;
        if (v?.ok) return `✓ ${lbl} → ${(v.recipients || []).join(", ")}`;
        if (v?.error) return `✗ ${lbl}: ${v.error}`;
        return `– ${lbl}: ${v?.skipped || "dilewati"}`;
      });
      if (data.any_sent) {
        toast.success(data.message || "Email terkirim", { description: lines.join("\n"), duration: 8000 });
      } else {
        toast.error(data.message || "Tidak ada email terkirim", { description: lines.join("\n"), duration: 8000 });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim"); }
    setTesting(false);
  };
  if (loading) return <Card className="p-8 text-center text-stone-400">Memuat...</Card>;
  const setRec = (key, idx, val) => {
    const arr = [...(s[key] || [])];
    arr[idx] = val;
    setS({ ...s, [key]: arr });
  };
  const addRec = (key) => setS({ ...s, [key]: [...(s[key] || []), ""] });
  const delRec = (key, idx) => setS({ ...s, [key]: (s[key] || []).filter((_, i) => i !== idx) });
  return (
    <Card className="p-5 space-y-5" data-testid="settings-payment-reminder">
      <div>
        <h3 className="text-base font-semibold text-stone-900">Pengingat Pembayaran via Email</h3>
        <p className="text-xs text-stone-500 mt-1 max-w-2xl">
          Kirim ringkasan email harian ke daftar penerima tentang Invoice belum terbayar (H-3 / H-1 / Telat)
          dan Pengeluaran/Tagihan Vendor yang mendekati jatuh tempo. Menggunakan kredensial Gmail SMTP yang
          dikonfigurasi di tab &quot;Email Stok Harian&quot;.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Jam Kirim (WIB)"><Input type="time" value={s.reminder_time || "08:00"} onChange={(e) => setS({ ...s, reminder_time: e.target.value })} data-testid="reminder-time" /></Field>
        <Field label="Window (hari sebelum jatuh tempo)"><Input type="number" min="1" max="14" value={s.reminder_days_ahead || 3} onChange={(e) => setS({ ...s, reminder_days_ahead: e.target.value })} data-testid="reminder-window" /></Field>
      </div>

      {/* Invoice block */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.invoice_reminder_enabled} onChange={(e) => setS({ ...s, invoice_reminder_enabled: e.target.checked })} data-testid="inv-reminder-toggle" />
          <span className="font-semibold text-sm">Pengingat Invoice — Digest ke Admin / Finance</span>
        </label>
        <p className="text-[11px] text-stone-500">Satu email berisi ringkasan SEMUA invoice (Overdue/H-1/H-3). Tujuan email admin atau finance.</p>
        {(s.invoice_reminder_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`inv-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("invoice_reminder_recipients", i, e.target.value)} placeholder="email@example.com" />
            <button onClick={() => delRec("invoice_reminder_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("invoice_reminder_recipients")} className="!text-xs" data-testid="inv-rec-add">+ Tambah Penerima</Button>
        <Button variant="secondary" onClick={() => sendTest("invoice_digest")} disabled={testing} className="!text-xs ml-2" data-testid="test-invoice-digest">Kirim Test</Button>

        {/* Iter v34t (#2) — Per-client invoice reminder */}
        <div className="pt-3 mt-3 border-t border-stone-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!s.invoice_reminder_to_client_enabled} onChange={(e) => setS({ ...s, invoice_reminder_to_client_enabled: e.target.checked })} data-testid="inv-client-toggle" />
            <span className="font-semibold text-sm">Pengingat Invoice — Per-Klien (kirim ke email klien)</span>
          </label>
          <p className="text-[11px] text-stone-500 mt-1">Setiap klien menerima email berisi DAFTAR INVOICE milik mereka sendiri. Email diambil dari record Klien atau akun user klien (account_type=klien).</p>
          <Button variant="secondary" onClick={() => sendTest("invoice_client")} disabled={testing} className="!text-xs mt-2" data-testid="test-invoice-client">Kirim Test</Button>
        </div>
      </div>

      {/* Expense block */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.expense_reminder_enabled} onChange={(e) => setS({ ...s, expense_reminder_enabled: e.target.checked })} data-testid="exp-reminder-toggle" />
          <span className="font-semibold text-sm">Pengingat Tagihan Vendor (Expense) Jatuh Tempo</span>
        </label>
        <p className="text-[11px] text-stone-500">Tujuan email (admin finance). Tekan + untuk menambah.</p>
        {(s.expense_reminder_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`exp-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("expense_reminder_recipients", i, e.target.value)} placeholder="email@example.com" />
            <button onClick={() => delRec("expense_reminder_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("expense_reminder_recipients")} className="!text-xs" data-testid="exp-rec-add">+ Tambah Penerima</Button>
        <Button variant="secondary" onClick={() => sendTest("expense_digest")} disabled={testing} className="!text-xs ml-2" data-testid="test-expense-digest">Kirim Test</Button>
      </div>

      {/* Iter v34t (#3) — PO Upload notification */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.po_upload_email_enabled} onChange={(e) => setS({ ...s, po_upload_email_enabled: e.target.checked })} data-testid="po-upload-toggle" />
          <span className="font-semibold text-sm">Notifikasi Email PO Baru Diupload</span>
        </label>
        <p className="text-[11px] text-stone-500">Setiap kali ada PO baru dibuat, kirim notifikasi email berisi nomor PO, klien, dan daftar item. Tujuan email (admin/finance).</p>
        {(s.po_upload_email_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`po-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("po_upload_email_recipients", i, e.target.value)} placeholder="email@example.com" />
            <button onClick={() => delRec("po_upload_email_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("po_upload_email_recipients")} className="!text-xs" data-testid="po-rec-add">+ Tambah Penerima</Button>
        <Button variant="secondary" onClick={() => sendTest("po_upload")} disabled={testing} className="!text-xs ml-2" data-testid="test-po-upload">Kirim Test</Button>
      </div>

      {/* Iter v34an (#2) — Surat Jalan Upload notification */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.sj_upload_email_enabled} onChange={(e) => setS({ ...s, sj_upload_email_enabled: e.target.checked })} data-testid="sj-upload-toggle" />
          <span className="font-semibold text-sm">Notifikasi Email Surat Jalan Baru</span>
        </label>
        <p className="text-[11px] text-stone-500">Setiap kali ada Surat Jalan baru dibuat, kirim notifikasi email berisi ringkasan SJ. File SJ (jika ada) otomatis dilampirkan.</p>
        {(s.sj_upload_email_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`sj-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("sj_upload_email_recipients", i, e.target.value)} placeholder="email@example.com" />
            <button onClick={() => delRec("sj_upload_email_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("sj_upload_email_recipients")} className="!text-xs" data-testid="sj-rec-add">+ Tambah Penerima</Button>
        <Button variant="secondary" onClick={() => sendTest("sj_upload")} disabled={testing} className="!text-xs ml-2" data-testid="test-sj-upload">Kirim Test</Button>
      </div>

      {/* Iter v34au (#2) — Leave request approval notification */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.leave_request_email_enabled} onChange={(e) => setS({ ...s, leave_request_email_enabled: e.target.checked })} data-testid="leave-req-toggle" />
          <span className="font-semibold text-sm">Notifikasi Email Pengajuan Cuti/Izin</span>
        </label>
        <p className="text-[11px] text-stone-500">Kirim email ke HR/atasan setiap kali ada pengajuan Cuti Tahunan, Cuti Khusus, Izin, atau Sakit yang butuh persetujuan.</p>
        {(s.leave_request_email_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`leave-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("leave_request_email_recipients", i, e.target.value)} placeholder="hr@example.com" />
            <button onClick={() => delRec("leave_request_email_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("leave_request_email_recipients")} className="!text-xs" data-testid="leave-rec-add">+ Tambah Penerima</Button>
      </div>

      {/* Iter v34cr — Peringatan Plafon Kredit Klien */}
      <div className="border border-stone-200 p-4 bg-stone-50/40 space-y-3" data-testid="credit-alert-block">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.credit_limit_alert_enabled} onChange={(e) => setS({ ...s, credit_limit_alert_enabled: e.target.checked })} data-testid="credit-alert-toggle" />
          <span className="font-semibold text-sm">Peringatan Plafon Kredit Klien</span>
        </label>
        <p className="text-[11px] text-stone-500">
          Cek otomatis 1× sehari. Dua tahap: <b>peringatan</b> saat pemakaian plafon mencapai ambang di bawah,
          dan <b>melebihi plafon</b> saat mencapai 100% (Surat Jalan baru otomatis diblokir).
          Dihitung dari tagihan yang sudah <b>jatuh tempo</b>.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Ambang Peringatan (%)">
            <Input type="number" min="1" max="100" value={s.credit_limit_alert_threshold_pct ?? 80} onChange={(e) => setS({ ...s, credit_limit_alert_threshold_pct: e.target.value })} data-testid="credit-alert-threshold" />
          </Field>
          <Field label="Jam Kirim (WIB)">
            <Input type="time" value={s.credit_limit_alert_time || "08:30"} onChange={(e) => setS({ ...s, credit_limit_alert_time: e.target.value })} data-testid="credit-alert-time" />
          </Field>
          <Field label="Jeda Anti-Spam (hari)" hint="1 klien maks 1 email per tahap dalam periode ini">
            <Input type="number" min="0" max="60" value={s.credit_limit_alert_cooldown_days ?? 7} onChange={(e) => setS({ ...s, credit_limit_alert_cooldown_days: e.target.value })} data-testid="credit-alert-cooldown" />
          </Field>
        </div>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
          <input type="checkbox" checked={s.credit_limit_alert_include_marketing_roles !== false} onChange={(e) => setS({ ...s, credit_limit_alert_include_marketing_roles: e.target.checked })} data-testid="credit-alert-marketing-roles" />
          Kirim juga otomatis ke semua user dengan role <b>marketing</b>
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-xs text-stone-700">
          <input type="checkbox" checked={!!s.credit_limit_alert_to_client_enabled} onChange={(e) => setS({ ...s, credit_limit_alert_to_client_enabled: e.target.checked })} data-testid="credit-alert-to-client" />
          Kirim juga ke <b>klien</b> (email akun portal klien → email master Klien → override manual di halaman Plafon)
        </label>
        <p className="text-[11px] text-stone-500 pt-1">Daftar email tim marketing / finance:</p>
        {(s.credit_limit_alert_recipients || []).map((em, i) => (
          <div key={i} className="flex gap-2" data-testid={`credit-alert-rec-${i}`}>
            <Input value={em} onChange={(e) => setRec("credit_limit_alert_recipients", i, e.target.value)} placeholder="marketing@example.com" />
            <button onClick={() => delRec("credit_limit_alert_recipients", i)} className="text-red-500 px-2">×</button>
          </div>
        ))}
        <Button variant="ghost" onClick={() => addRec("credit_limit_alert_recipients")} className="!text-xs" data-testid="credit-alert-rec-add">+ Tambah Penerima</Button>
        {s.credit_limit_alert_last_sent_at && (
          <p className="text-[11px] text-stone-500">Terakhir terkirim: {s.credit_limit_alert_last_sent_at}</p>
        )}
      </div>

      <div className="flex gap-2 justify-end pt-3 border-t border-stone-200">
        <Button variant="ghost" onClick={() => sendTest()} disabled={testing} data-testid="payment-reminder-test">{testing ? "Mengirim..." : "Kirim Test SEMUA"}</Button>
        <Button onClick={save} disabled={saving} data-testid="payment-reminder-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
      </div>
      {s.last_invoice_sent_at && <p className="text-[10px] text-stone-400">Invoice terakhir dikirim: {s.last_invoice_sent_at}</p>}
      {s.last_expense_sent_at && <p className="text-[10px] text-stone-400">Expense terakhir dikirim: {s.last_expense_sent_at}</p>}
    </Card>
  );
}

// Iter v34q (#2) — Toggle ON/OFF AI assistant per channel (klien CS & karyawan HR).
function AIAssistantSettings() {
  const [s, setS] = useState({ ai_cs_enabled: true, ai_hr_enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => {
      setS({
        ai_cs_enabled: r.data?.ai_cs_enabled !== false,
        ai_hr_enabled: r.data?.ai_hr_enabled !== false,
      });
    }).finally(() => setLoading(false));
  }, []);
  const save = async (next) => {
    setSaving(true);
    try {
      await api.patch("/settings", next);
      setS({ ...s, ...next });
      toast.success("Pengaturan AI tersimpan");
    } catch (_e) {
      toast.error("Gagal menyimpan");
    } finally { setSaving(false); }
  };
  if (loading) return <Card className="p-8 text-center text-stone-400">Memuat...</Card>;
  return (
    <Card className="p-5 space-y-5" data-testid="settings-ai">
      <div>
        <h3 className="text-base font-semibold text-stone-900">AI Assistant — Toggle Global</h3>
        <p className="text-xs text-stone-500 mt-1 max-w-2xl">
          Matikan AI auto-reply secara global per kanal. Saat dimatikan, pesan masuk dari klien/karyawan
          tetap diterima namun tidak dibalas otomatis oleh AI — admin/HR yang akan membalas secara manual.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ToggleRow
          icon={MessageSquareText}
          title="AI CS untuk Portal Klien"
          desc="Bot Claude Sonnet 4.5 yang membalas Chat CS klien (membantu cek PO, SJ, Invoice, harga). Saat OFF, hanya admin yang membalas."
          checked={s.ai_cs_enabled}
          onToggle={(v) => save({ ai_cs_enabled: v })}
          disabled={saving}
          testid="ai-cs-toggle"
        />
        <ToggleRow
          icon={UserRound}
          title="AI HR untuk Portal Pekerja"
          desc="Bot Claude Sonnet 4.5 yang membantu karyawan terkait absensi & pembayaran gaji. Saat OFF, hanya HR yang membalas."
          checked={s.ai_hr_enabled}
          onToggle={(v) => save({ ai_hr_enabled: v })}
          disabled={saving}
          testid="ai-hr-toggle"
        />
      </div>

      <div className="text-[11px] text-stone-500 border-l-2 border-terra-300 bg-terra-50/40 pl-3 py-2">
        <strong>Tips:</strong> Gunakan toggle ini saat masa libur atau saat AI memberi jawaban yang kurang tepat.
        Pesan tetap masuk ke Inbox HR / Inbox CS dan dapat dibalas manual seperti biasa.
      </div>
    </Card>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onToggle, disabled, testid }) {
  return (
    <div className={`border p-4 transition-colors ${checked ? "border-terra-300 bg-terra-50/30" : "border-stone-200 bg-stone-50/40"}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 mt-0.5 ${checked ? "text-terra-600" : "text-stone-400"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold text-sm text-stone-900">{title}</p>
            <label className="relative inline-flex items-center cursor-pointer" data-testid={testid}>
              <input
                type="checkbox"
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onToggle(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-stone-300 peer-focus:ring-2 peer-focus:ring-terra-300 peer-checked:bg-terra-600 transition-colors relative">
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
              </div>
            </label>
          </div>
          <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">{desc}</p>
          <div className="mt-2">
            <Badge tone={checked ? "success" : "warn"}>{checked ? "Aktif" : "Nonaktif"}</Badge>
          </div>
        </div>
      </div>
    </div>
  );
}

function InputHistoryManager() {
  const [rows, setRows] = useState([]);
  const [fieldFilter, setFieldFilter] = useState("");
  const [search, setSearch] = useState("");
  const load = async () => {
    const r = await api.get("/input-history", { params: { field: fieldFilter || undefined, q: search || undefined } });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [fieldFilter]);
  const allFields = Array.from(new Set(rows.map((r) => r.field))).sort();
  const filtered = search ? rows.filter((r) => r.value.toLowerCase().includes(search.toLowerCase())) : rows;
  const delOne = async (id) => {
    if (!window.confirm("Hapus item ini dari riwayat input?")) return;
    await api.delete(`/input-history/${id}`);
    toast.success("Dihapus");
    load();
  };
  const delAll = async () => {
    const msg = fieldFilter
      ? `Hapus SEMUA riwayat untuk field "${fieldFilter}"?`
      : "Hapus SEMUA riwayat input (semua field)?";
    if (!window.confirm(msg)) return;
    const r = await api.delete("/input-history", { params: { field: fieldFilter || undefined } });
    toast.success(`${r.data.deleted} item terhapus`);
    load();
  };
  return (
    <Card className="p-4 space-y-3" data-testid="settings-input-history">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-base font-semibold text-stone-900">Riwayat Input Manual</h3>
          <p className="text-xs text-stone-500 mt-1 max-w-xl">Daftar nilai yang pernah diketik secara manual pada formulir SJ/PO dan akan muncul sebagai dropdown autocomplete. Hapus entry yang sudah tidak relevan supaya dropdown tetap rapi.</p>
        </div>
        <Button variant="ghost" onClick={delAll} className="text-red-700 hover:bg-red-50" data-testid="ih-delete-all-btn">
          <Trash2 className="h-3.5 w-3.5" /> Hapus {fieldFilter ? `Field "${fieldFilter}"` : "Semua Field"}
        </Button>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={fieldFilter} onChange={(e) => setFieldFilter(e.target.value)} className="!w-auto" data-testid="ih-field-filter">
          <option value="">Semua Field</option>
          {allFields.map((f) => <option key={f} value={f}>{f}</option>)}
        </Select>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nilai…" className="max-w-xs" data-testid="ih-search" />
      </div>
      <div className="border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-2 px-3">Field</th>
              <th className="py-2 px-3">Nilai</th>
              <th className="py-2 px-3 text-right">Dipakai</th>
              <th className="py-2 px-3">Terakhir</th>
              <th className="py-2 px-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center text-stone-400 text-xs">Tidak ada riwayat input.</td></tr>
            )}
            {filtered.map((r, idx) => (
              <tr key={r.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`ih-row-${idx}`}>
                <td className="py-2 px-3 font-mono text-xs text-stone-600">{r.field}</td>
                <td className="py-2 px-3 text-stone-900">{r.value}</td>
                <td className="py-2 px-3 text-right font-mono text-xs text-stone-500">×{r.use_count}</td>
                <td className="py-2 px-3 font-mono text-[10px] text-stone-500">{r.last_used_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => delOne(r.id)} className="text-stone-400 hover:text-red-700" data-testid={`ih-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CompanyProfiles() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { name: "", code: "JP", address: "", phone: "", npwp: "", is_default: false, logo_path: "", logo_id: "", logo_filename: "" };
  const [form, setForm] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const load = () => api.get("/companies").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      if (edit) await api.patch(`/companies/${edit.id}`, form);
      else await api.post("/companies", form);
      toast.success("Tersimpan"); setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error("Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus profil?")) return; await api.delete(`/companies/${id}`); load(); };
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "logo");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: r.data.path, logo_id: r.data.id, logo_filename: r.data.filename }));
      toast.success("Logo terunggah");
    } catch (err) { toast.error("Gagal upload logo (gunakan PNG/JPG)"); }
    finally { setUploading(false); }
  };
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Profil Perusahaan / Kop Surat</p>
        <Button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} data-testid="add-company-btn"><Plus className="h-4 w-4" />Tambah</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
          <th className="py-3 px-3">Logo</th><th className="py-3 px-3">Nama</th><th className="py-3 px-3">Kode</th><th className="py-3 px-3">NPWP</th><th className="py-3 px-3">Alamat</th><th className="py-3 px-3">Default</th><th className="py-3 px-3 text-right">Aksi</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3">{r.logo_id ? <LogoThumb fileId={r.logo_id} /> : <span className="text-stone-300">—</span>}</td>
              <td className="py-2.5 px-3 font-medium">{r.name}</td>
              <td className="py-2.5 px-3 font-mono">{r.code}</td>
              <td className="py-2.5 px-3 font-mono text-xs">{r.npwp || "—"}</td>
              <td className="py-2.5 px-3 text-stone-600 text-xs">{r.address}</td>
              <td className="py-2.5 px-3">{r.is_default && <Badge tone="terra">DEFAULT</Badge>}</td>
              <td className="py-2.5 px-3 text-right">
                <button onClick={() => { setEdit(r); setForm({ ...blank, ...r }); setOpen(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-stone-400">Belum ada profil.</td></tr>}
        </tbody>
      </table>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Profil" : "Tambah Profil Perusahaan"} size="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nama Perusahaan" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-form-name" /></Field>
          <Field label="Kode (Bagian dari No Invoice)" hint="Contoh: SCT1, JP, JK"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} /></Field>
          <Field label="NPWP"><Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} /></Field>
          <Field label="Telepon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Alamat" hint="Tampil sebagai kop surat"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <label className="flex items-center gap-2 text-sm self-end">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} /> Set sebagai default
          </label>
          <div className="md:col-span-2">
            <Field label="Logo Perusahaan" hint="PNG / JPG. Tampil pada kop semua dokumen PDF.">
              <div className="flex items-center gap-4">
                {form.logo_id && <LogoThumb fileId={form.logo_id} size={56} />}
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-stone-300 cursor-pointer hover:bg-stone-50 text-sm">
                  <Upload className="h-4 w-4" />{uploading ? "Mengunggah…" : (form.logo_path ? "Ganti logo" : "Pilih logo")}
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={uploadLogo} className="hidden" data-testid="company-logo-input" />
                </label>
                {form.logo_path && (
                  <button type="button" onClick={() => setForm({ ...form, logo_path: "", logo_id: "", logo_filename: "" })} className="text-xs text-stone-500 hover:text-red-700">Hapus logo</button>
                )}
              </div>
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-stone-200">
          <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={submit} data-testid="save-company-btn">Simpan</Button>
        </div>
      </Modal>
    </Card>
  );
}

function LogoThumb({ fileId, size = 32 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url; let mounted = true;
    api.get(`/files/${fileId}`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      if (mounted) setSrc(url);
    }).catch(() => {});
    return () => { mounted = false; if (url) URL.revokeObjectURL(url); };
  }, [fileId]);
  if (!src) return <div className="bg-stone-100 border border-stone-200" style={{ width: size, height: size }} />;
  return <img src={src} alt="logo" className="object-contain border border-stone-200 bg-white" style={{ width: size, height: size }} />;
}

function Signatories() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { name: "", location: "", position: "" };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/signatories").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      if (edit) await api.patch(`/signatories/${edit.id}`, form);
      else await api.post("/signatories", form);
      toast.success("Tersimpan"); setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error("Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/signatories/${id}`); load(); };
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Penanda Tangan</p>
        <Button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} data-testid="add-signatory-btn"><Plus className="h-4 w-4" />Tambah</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
          <th className="py-3 px-3">Nama</th><th className="py-3 px-3">Tempat (Lokasi)</th><th className="py-3 px-3">Jabatan</th><th className="py-3 px-3 text-right">Aksi</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-stone-400">Belum ada penanda tangan.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3 font-medium">{r.name}</td>
              <td className="py-2.5 px-3 text-stone-600">{r.location || "—"}</td>
              <td className="py-2.5 px-3 text-stone-600">{r.position || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                <button onClick={() => { setEdit(r); setForm({ name: r.name, location: r.location || "", position: r.position || "" }); setOpen(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Penanda Tangan" : "Tambah Penanda Tangan"}>
        <div className="space-y-4">
          <Field label="Nama" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sig-form-name" /></Field>
          <Field label="Tempat (Lokasi)" hint='Akan tampil pada Kwitansi: "{lokasi}, {tanggal}"'><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Palembang" data-testid="sig-form-location" /></Field>
          <Field label="Jabatan" hint="Tampil di bawah nama penanda tangan"><Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="Direktur" data-testid="sig-form-position" /></Field>
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="save-sig-btn">Simpan</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

function Banks() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { bank_name: "", branch: "", account_no: "", account_name: "", company_id: "" };
  const [form, setForm] = useState(blank);
  const load = () => Promise.all([api.get("/banks"), api.get("/companies")]).then(([b, c]) => { setRows(b.data); setCompanies(c.data); });
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      if (edit) await api.patch(`/banks/${edit.id}`, form);
      else await api.post("/banks", form);
      toast.success("Tersimpan"); setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error("Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/banks/${id}`); load(); };
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Rekening Bank</p>
        <Button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} data-testid="add-bank-btn"><Plus className="h-4 w-4" />Tambah</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
          <th className="py-3 px-3">Bank</th><th className="py-3 px-3">Cabang</th><th className="py-3 px-3">No Rekening</th><th className="py-3 px-3">Atas Nama</th><th className="py-3 px-3">Perusahaan</th><th className="py-3 px-3 text-right">Aksi</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3 font-medium">{r.bank_name}</td>
              <td className="py-2.5 px-3 text-stone-600">{r.branch}</td>
              <td className="py-2.5 px-3 font-mono">{r.account_no}</td>
              <td className="py-2.5 px-3">{r.account_name}</td>
              <td className="py-2.5 px-3 text-stone-600 text-xs">{companies.find((c) => c.id === r.company_id)?.name || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                <button onClick={() => { setEdit(r); setForm(r); setOpen(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-400">Belum ada rekening.</td></tr>}
        </tbody>
      </table>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Bank" : "Tambah Rekening Bank"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nama Bank" required><Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-form-name" /></Field>
          <Field label="Cabang"><Input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} /></Field>
          <Field label="No Rekening" required><Input value={form.account_no} onChange={(e) => setForm({ ...form, account_no: e.target.value })} className="font-mono" /></Field>
          <Field label="Atas Nama" required><Input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} /></Field>
          <Field label="Perusahaan (opsional)">
            <Select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
              <option value="">— Semua —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-stone-200">
          <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
          <Button onClick={submit} data-testid="save-bank-btn">Simpan</Button>
        </div>
      </Modal>
    </Card>
  );
}

function MasterList({ kind, label }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get(`/masters/${kind}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [kind]);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post(`/masters/${kind}`, { name: name.trim() }); setName(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus?")) return; await api.delete(`/masters/${kind}/${id}`); load(); };
  return (
    <Card className="p-5">
      <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600 mb-3">{label}</p>
      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={`Nama ${label.toLowerCase()} baru`} data-testid={`master-${kind}-input`} />
        <Button onClick={add} data-testid={`master-${kind}-add`}><Plus className="h-4 w-4" />Tambah</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.length === 0 && <p className="text-sm text-stone-400">Belum ada data.</p>}
        {rows.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-2 bg-stone-100 px-3 py-1.5 text-sm">
            {r.name}
            <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </Card>
  );
}

function Clients() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const blank = { name: "", address: "", phone: "", npwp: "", email: "" };
  const [form, setForm] = useState(blank);
  const load = () => api.get("/clients").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      if (edit) await api.patch(`/clients/${edit.id}`, form);
      else await api.post("/clients", form);
      toast.success("Tersimpan"); setOpen(false); setEdit(null); setForm(blank); load();
    } catch (e) { toast.error("Gagal"); }
  };
  const del = async (id) => { if (!window.confirm("Hapus klien?")) return; await api.delete(`/clients/${id}`); load(); };
  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
        <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Master Klien</p>
        <Button onClick={() => { setEdit(null); setForm(blank); setOpen(true); }} data-testid="add-client-btn"><Plus className="h-4 w-4" />Tambah</Button>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
          <th className="py-3 px-3">Nama</th><th className="py-3 px-3">Alamat</th><th className="py-3 px-3">Telp</th><th className="py-3 px-3">Email</th><th className="py-3 px-3">NPWP</th><th className="py-3 px-3 text-right">Aksi</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3 font-medium">{r.name}</td>
              <td className="py-2.5 px-3 text-stone-600 text-xs">{r.address || "—"}</td>
              <td className="py-2.5 px-3 font-mono text-xs">{r.phone || "—"}</td>
              <td className="py-2.5 px-3 text-xs">{r.email || "—"}</td>
              <td className="py-2.5 px-3 font-mono text-xs">{r.npwp || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                <button onClick={() => { setEdit(r); setForm({ name: r.name, address: r.address || "", phone: r.phone || "", npwp: r.npwp || "", email: r.email || "" }); setOpen(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-400">Belum ada klien.</td></tr>}
        </tbody>
      </table>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Klien" : "Tambah Klien"}>
        <div className="space-y-4">
          <Field label="Nama" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-form-name" /></Field>
          <Field label="Alamat"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="Telepon"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Email" hint="Dipakai untuk pengingat tagihan & peringatan plafon kredit"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="finance@klien.co.id" data-testid="client-form-email" /></Field>
          <Field label="NPWP"><Input value={form.npwp} onChange={(e) => setForm({ ...form, npwp: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="save-client-btn">Simpan</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}


const RESET_OPTIONS = [
  { key: "prices", label: "Daftar Harga" },
  { key: "pos", label: "Purchase Orders" },
  { key: "sj", label: "Surat Jalan" },
  { key: "stocks", label: "Stok" },
  { key: "expenses", label: "Pengeluaran" },
  { key: "invoices", label: "Invoice" },
  { key: "clients", label: "Klien" },
  { key: "alerts", label: "Notifikasi" },
  { key: "logs", label: "Log Sistem" },
  { key: "files", label: "Catatan File" },
  { key: "companies", label: "Profil Perusahaan" },
  { key: "banks", label: "Rekening Bank" },
  { key: "all", label: "SEMUA DATA DEFAULT" },
];

function ResetData() {
  const reset = async (target, label) => {
    const expected = target === "all" ? "HAPUS SEMUA" : label;
    const v = window.prompt(`Ketik "${expected}" untuk konfirmasi penghapusan ${label}.`);
    if (v !== expected) {
      toast.error("Konfirmasi tidak cocok. Pembatalan.");
      return;
    }
    try {
      const r = await api.post(`/admin/reset?target=${target}`);
      const removed = r.data.removed || {};
      const total = Object.values(removed).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Berhasil menghapus ${total} record`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 p-4">
        <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Tindakan tidak dapat dikembalikan</p>
          <p>Reset menghapus seluruh record pada koleksi yang dipilih. Akun super admin dan role default tidak terpengaruh.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {RESET_OPTIONS.map((x) => (
          <div key={x.key} className={`border p-3 ${x.key === "all" ? "border-red-300 bg-red-50/40" : "border-stone-200"}`}>
            <p className={`font-medium ${x.key === "all" ? "text-red-700" : "text-stone-900"}`}>{x.label}</p>
            <Button variant={x.key === "all" ? "danger" : "secondary"} className="mt-2 !py-1.5 !px-3 text-xs" onClick={() => reset(x.key, x.label)} data-testid={`reset-${x.key}`}>
              <Trash2 className="h-3.5 w-3.5" />Hapus
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function QuoteNoteTemplates() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });

  const load = () => api.get("/quote-note-templates").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Judul & isi catatan wajib"); return; }
    try {
      if (edit) await api.patch(`/quote-note-templates/${edit.id}`, form);
      else await api.post("/quote-note-templates", form);
      toast.success("Tersimpan"); setOpen(false); setForm({ title: "", body: "" }); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus template?")) return;
    await api.delete(`/quote-note-templates/${id}`); load();
  };

  return (
    <Card>
      <div className="flex items-center justify-between p-4 border-b border-stone-200">
        <div>
          <h3 className="text-sm font-semibold text-stone-900">Template Catatan Penawaran</h3>
          <p className="text-xs text-stone-500">Daftar template catatan. Judul hanya tampil di dropdown — yang dicetak di PDF Penawaran hanya isi catatan.</p>
        </div>
        <Button onClick={() => { setEdit(null); setForm({ title: "", body: "" }); setOpen(true); }} data-testid="add-note-tmpl-btn"><Plus className="h-4 w-4" /> Tambah Template</Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-3 px-4 w-1/3">Judul (Label Dropdown)</th>
            <th className="py-3 px-4">Isi Catatan (yang dicetak)</th>
            <th className="py-3 px-4 text-right w-24">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-stone-400">Belum ada template.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-4 font-medium">{r.title}</td>
              <td className="py-2.5 px-4 text-stone-600 whitespace-pre-wrap text-xs">{r.body}</td>
              <td className="py-2.5 px-4 text-right">
                <button onClick={() => { setEdit(r); setForm({ title: r.title, body: r.body }); setOpen(true); }} className="text-stone-500 hover:text-terra-700 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Edit Template Catatan" : "Tambah Template Catatan"}>
        <div className="space-y-4">
          <Field label="Judul" required hint="Hanya untuk identifikasi di dropdown — TIDAK dicetak di PDF">
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="contoh: Termin Pembayaran 50%" data-testid="note-tmpl-title" />
          </Field>
          <Field label="Isi Catatan" required hint="Teks ini akan disisipkan ke field Catatan saat dipilih">
            <textarea rows={5} className="w-full border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terra-500" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="note-tmpl-body" />
          </Field>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="note-tmpl-save">{edit ? "Simpan" : "Tambah"}</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}


// Iter v29 — Department Categories (Batching Plant / Proyek / Penjualan Sungai Baung)
function DepartmentCategories() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/department-categories").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/department-categories", { name: name.trim() }); setName(""); toast.success("Ditambahkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const del = async (id, defaultFlag) => {
    if (defaultFlag && !window.confirm("Kategori ini adalah bawaan sistem. Tetap hapus?")) return;
    if (!defaultFlag && !window.confirm("Hapus kategori ini?")) return;
    try { await api.delete(`/department-categories/${id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  return (
    <Card className="p-5" data-testid="settings-dept-categories">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Kategori Departemen</p>
          <p className="text-xs text-stone-500 mt-1 max-w-xl">Pilihan dropdown saat karyawan mendaftar mandiri di halaman <code>/register/karyawan</code>. Tiga kategori default sudah disediakan dan boleh diedit/dihapus.</p>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nama kategori baru..." data-testid="dept-cat-input" />
        <Button onClick={add} data-testid="dept-cat-add"><Plus className="h-4 w-4" /> Tambah</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {rows.length === 0 && <p className="text-sm text-stone-400">Belum ada kategori.</p>}
        {rows.map((r) => (
          <span key={r.id} className="inline-flex items-center gap-2 bg-stone-100 px-3 py-1.5 text-sm" data-testid={`dept-cat-${r.id}`}>
            {r.name}
            {r.is_default && <Badge tone="emerald">default</Badge>}
            <button onClick={() => del(r.id, r.is_default)} className="text-stone-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
    </Card>
  );
}




// =============================================================================
// Iter v34z (#8) — Custom Document Number Format settings (Vendor PO + Batching PO)
// =============================================================================
function DocNumberFormatSettings() {
  const [formats, setFormats] = useState({ vendor_po: "", batching_po: "", batching_sj: "", material_sj: "" });
  const [defaults, setDefaults] = useState({});
  const [tokens, setTokens] = useState([]);
  const [preview, setPreview] = useState({ vendor_po: "", batching_po: "", batching_sj: "", material_sj: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/doc-number-formats");
      setFormats(r.data.formats || {});
      setDefaults(r.data.defaults || {});
      setTokens(r.data.tokens || []);
      const [v, b, s, m] = await Promise.all([
        api.get("/doc-number-formats/preview", { params: { doc_type: "vendor_po" } }),
        api.get("/doc-number-formats/preview", { params: { doc_type: "batching_po" } }),
        api.get("/doc-number-formats/preview", { params: { doc_type: "batching_sj" } }),
        api.get("/doc-number-formats/preview", { params: { doc_type: "material_sj" } }),
      ]);
      setPreview({ vendor_po: v.data.preview, batching_po: b.data.preview, batching_sj: s.data.preview, material_sj: m.data.preview });
    } catch (e) { toast.error("Gagal memuat format"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/doc-number-formats", formats);
      toast.success("Format nomor disimpan");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const restoreDefault = (key) => setFormats((f) => ({ ...f, [key]: defaults[key] || "" }));

  return (
    <Card>
      <div className="p-5 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-stone-900">Format Nomor PO Custom</h3>
          <p className="text-xs text-stone-500 mt-1">Atur template auto-generate nomor PO Vendor (Procurement) dan PO Vendor Batching.</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs space-y-1">
          <div className="font-semibold text-blue-800">Token yang tersedia:</div>
          <div className="flex flex-wrap gap-1">
            {tokens.map((t) => (<code key={t} className="bg-white px-1.5 py-0.5 rounded border border-blue-200 font-mono">{t}</code>))}
          </div>
          <div className="text-stone-600 pt-1">
            <strong>{"{YYYY}"}</strong> = 2026, <strong>{"{YY}"}</strong> = 26, <strong>{"{YYMM}"}</strong> = 2606,
            <strong> {"{MM}"}</strong> = 06, <strong>{"{DD}"}</strong> = 25,
            <strong> {"{SEQ:4}"}</strong> = 0001 (zero-padded sequence; ganti N untuk panjang lain).
          </div>
        </div>

        <div className="space-y-3">
          <Field label="Format Nomor — PO Vendor (Procurement umum)" hint={`Default: ${defaults.vendor_po || ""}`}>
            <div className="flex gap-2">
              <Input value={formats.vendor_po || ""} onChange={(e) => setFormats({ ...formats, vendor_po: e.target.value })} className="font-mono" data-testid="format-vendor-po" />
              <Button variant="secondary" type="button" onClick={() => restoreDefault("vendor_po")}>Default</Button>
            </div>
            <div className="text-xs text-stone-500 mt-1">Preview nomor berikutnya: <code className="font-mono bg-stone-100 px-1 rounded" data-testid="preview-vendor-po">{preview.vendor_po || "—"}</code></div>
          </Field>

          <Field label="Format Nomor — PO Vendor Batching" hint={`Default: ${defaults.batching_po || ""}`}>
            <div className="flex gap-2">
              <Input value={formats.batching_po || ""} onChange={(e) => setFormats({ ...formats, batching_po: e.target.value })} className="font-mono" data-testid="format-batching-po" />
              <Button variant="secondary" type="button" onClick={() => restoreDefault("batching_po")}>Default</Button>
            </div>
            <div className="text-xs text-stone-500 mt-1">Preview nomor berikutnya: <code className="font-mono bg-stone-100 px-1 rounded" data-testid="preview-batching-po">{preview.batching_po || "—"}</code></div>
          </Field>

          <Field label="Format Nomor — Surat Jalan Batching (Readymix)" hint={`Default: ${defaults.batching_sj || ""}`}>
            <div className="flex gap-2">
              <Input value={formats.batching_sj || ""} onChange={(e) => setFormats({ ...formats, batching_sj: e.target.value })} className="font-mono" data-testid="format-batching-sj" />
              <Button variant="secondary" type="button" onClick={() => restoreDefault("batching_sj")}>Default</Button>
            </div>
            <div className="text-xs text-stone-500 mt-1">Preview nomor berikutnya: <code className="font-mono bg-stone-100 px-1 rounded" data-testid="preview-batching-sj">{preview.batching_sj || "—"}</code></div>
          </Field>

          <Field label="Format Nomor — Surat Jalan Material (Batu/Pasir)" hint={`Default: ${defaults.material_sj || ""}`}>
            <div className="flex gap-2">
              <Input value={formats.material_sj || ""} onChange={(e) => setFormats({ ...formats, material_sj: e.target.value })} className="font-mono" data-testid="format-material-sj" />
              <Button variant="secondary" type="button" onClick={() => restoreDefault("material_sj")}>Default</Button>
            </div>
            <div className="text-xs text-stone-500 mt-1">Preview nomor berikutnya: <code className="font-mono bg-stone-100 px-1 rounded" data-testid="preview-material-sj">{preview.material_sj || "—"}</code></div>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
          <Button onClick={save} disabled={saving} data-testid="format-save"><Plus className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
        </div>
      </div>
    </Card>
  );
}


// =============================================================================
// Iter v34z (#9) — Alert Stok Minim Batching settings (toggle + recipients)
// =============================================================================
function StockAlertSettings() {
  const [s, setS] = useState({ stock_alert_enabled: false, stock_alert_recipients: [], reminder_time: "08:00" });
  const [newRecip, setNewRecip] = useState("");
  const [alerts, setAlerts] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get("/payment-reminder-settings");
    setS({
      stock_alert_enabled: !!r.data.stock_alert_enabled,
      stock_alert_recipients: r.data.stock_alert_recipients || [],
      reminder_time: r.data.reminder_time || "08:00",
    });
    const a = await api.get("/batching/stock-alerts").catch(() => ({ data: { alerts: [] } }));
    setAlerts(a.data?.alerts || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // PATCH to merge — need to read current full doc first to preserve other reminder fields.
      const cur = await api.get("/payment-reminder-settings");
      await api.patch("/payment-reminder-settings", {
        ...cur.data,
        stock_alert_enabled: !!s.stock_alert_enabled,
        stock_alert_recipients: s.stock_alert_recipients,
        reminder_time: s.reminder_time,
      });
      toast.success("Pengaturan tersimpan");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const addRecip = () => {
    const v = newRecip.trim();
    if (!v) return;
    setS({ ...s, stock_alert_recipients: [...s.stock_alert_recipients, v] });
    setNewRecip("");
  };
  const rmRecip = (i) => setS({ ...s, stock_alert_recipients: s.stock_alert_recipients.filter((_, j) => j !== i) });

  return (
    <Card>
      <div className="p-5 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-stone-900">Alert Stok Minim — Material Batching</h3>
          <p className="text-xs text-stone-500 mt-1">Kirim email harian otomatis jika ada material di bawah threshold safety stock (set <code>min_stock_kg</code> pada master material di halaman Stok Material Batching).</p>
        </div>

        {alerts.length > 0 ? (
          <div className="bg-red-50 border border-red-200 rounded p-3" data-testid="stock-alert-banner">
            <div className="font-semibold text-red-700 text-sm mb-1">⚠ {alerts.length} material saat ini di bawah threshold:</div>
            <ul className="text-xs space-y-0.5 text-red-700">
              {alerts.map((a) => (<li key={a.id}>{a.name}: <strong>{a.balance_kg.toLocaleString()} kg</strong> (min: {a.min_stock_kg.toLocaleString()} kg, kurang {a.short_kg.toLocaleString()} kg)</li>))}
            </ul>
          </div>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-emerald-700 text-xs">
            ✓ Semua material di atas threshold (atau belum ada material yang punya min_stock_kg).
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.stock_alert_enabled} onChange={(e) => setS({ ...s, stock_alert_enabled: e.target.checked })} data-testid="stock-alert-toggle" />
          <span className="text-sm font-medium">Aktifkan Email Alert Harian</span>
        </label>

        <Field label="Waktu kirim harian (HH:MM, Asia/Jakarta)">
          <Input type="time" value={s.reminder_time} onChange={(e) => setS({ ...s, reminder_time: e.target.value })} className="!w-32" />
        </Field>

        <Field label="Penerima Email (admin/purchasing)">
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input type="email" value={newRecip} onChange={(e) => setNewRecip(e.target.value)} placeholder="email@purchasing.id" />
              <Button variant="secondary" type="button" onClick={addRecip} data-testid="stock-alert-add-recip"><Plus className="h-3 w-3" /> Tambah</Button>
            </div>
            {s.stock_alert_recipients.map((r, i) => (
              <div key={i} className="flex items-center gap-2 bg-stone-50 border border-stone-200 rounded px-3 py-1.5 text-sm">
                <span className="flex-1">{r}</span>
                <button onClick={() => rmRecip(i)} className="text-stone-400 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </Field>

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
          <Button onClick={save} disabled={saving} data-testid="stock-alert-save"><Plus className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
        </div>
      </div>
    </Card>
  );
}


// =============================================================================
// Iter v34z (#4) — Email Template Editor (Advanced): per reminder type +
// per-bucket overrides + token substitution + live preview.
// =============================================================================
const REMINDER_TYPES = [
  { key: "invoice_digest", label: "Invoice Digest (Admin/Finance)", has_buckets: true },
  { key: "invoice_client", label: "Invoice Per-Klien", has_buckets: false },
  { key: "expense_digest", label: "Tagihan Vendor (Expense)", has_buckets: true },
  { key: "stock_alert", label: "Alert Stok Minim Batching", has_buckets: false },
];
const TOKENS = ["{today}", "{today_id}", "{count}", "{client_name}", "{company_name}", "{company_phone}", "{window}"];

function EmailTemplateSettings() {
  const [subTab, setSubTab] = useState("invoice_digest");
  const [tpl, setTpl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [saving, setSaving] = useState(false);

  const cur = REMINDER_TYPES.find((t) => t.key === subTab);

  const load = async (key) => {
    const r = await api.get(`/email-templates/${key}`);
    setTpl(r.data);
  };
  const renderPreview = async (key) => {
    try {
      const r = await api.post(`/email-templates/${key}/preview`, {});
      setPreviewHtml(r.data.html);
      setPreviewSubject(r.data.subject);
    } catch { setPreviewHtml(""); setPreviewSubject(""); }
  };

  useEffect(() => { load(subTab); renderPreview(subTab); }, [subTab]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        subject: tpl.subject || "",
        html_header: tpl.html_header || "",
        html_footer: tpl.html_footer || "",
        bucket_overrides: tpl.bucket_overrides || {},
      };
      await api.put(`/email-templates/${subTab}`, payload);
      toast.success("Template disimpan");
      await renderPreview(subTab);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset template ini ke default sistem?")) return;
    await api.delete(`/email-templates/${subTab}`);
    toast.success("Direset ke default");
    await load(subTab); await renderPreview(subTab);
  };
  const setBucket = (bkey, field, val) => {
    const bo = { ...(tpl.bucket_overrides || {}) };
    bo[bkey] = { ...(bo[bkey] || {}), [field]: val };
    setTpl({ ...tpl, bucket_overrides: bo });
  };

  if (!tpl) return <Card><div className="p-6 text-center text-stone-400">Memuat…</div></Card>;

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-5 space-y-3 border-b border-stone-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-stone-900">Template Email (Custom)</h3>
              <p className="text-xs text-stone-500 mt-1">Edit subject + header + footer + per-bucket text untuk setiap jenis email pengingat.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {REMINDER_TYPES.map((t) => (
              <button
                key={t.key}
                onClick={() => setSubTab(t.key)}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  subTab === t.key
                    ? "bg-terra-600 text-white font-semibold"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
                data-testid={`email-tpl-tab-${t.key}`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-5">
          {/* Editor */}
          <div className="space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs">
              <strong className="text-blue-800">Token:</strong>{" "}
              {TOKENS.map((t) => (
                <code key={t} className="inline-block bg-white px-1.5 py-0.5 rounded border border-blue-200 font-mono mr-1 mb-1">{t}</code>
              ))}
            </div>

            <Field label="Subject Email">
              <Input value={tpl.subject || ""} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} data-testid="email-tpl-subject" />
            </Field>

            <Field label="HTML Header (di atas tabel data)">
              <Textarea rows={4} value={tpl.html_header || ""} onChange={(e) => setTpl({ ...tpl, html_header: e.target.value })} className="font-mono text-xs" data-testid="email-tpl-header" />
            </Field>

            {cur.has_buckets && (
              <div className="border border-stone-200 rounded p-3 bg-stone-50/40 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-blue-600">Per-Bucket Override</div>
                {["overdue", "h1", "h3"].map((bk) => {
                  const bo = (tpl.bucket_overrides || {})[bk] || {};
                  return (
                    <div key={bk} className="space-y-1 border-l-2 border-stone-300 pl-3" data-testid={`email-tpl-bucket-${bk}`}>
                      <div className="text-xs font-semibold uppercase text-stone-600">{bk === "overdue" ? "Sudah Lewat" : bk === "h1" ? "H-1 (Besok)" : "H-3 (3 Hari Lagi)"}</div>
                      <Input placeholder="Judul section" value={bo.title || ""} onChange={(e) => setBucket(bk, "title", e.target.value)} className="text-xs" />
                      <Input placeholder="Intro text (opsional)" value={bo.intro || ""} onChange={(e) => setBucket(bk, "intro", e.target.value)} className="text-xs" />
                    </div>
                  );
                })}
              </div>
            )}

            <Field label="HTML Footer">
              <Textarea rows={3} value={tpl.html_footer || ""} onChange={(e) => setTpl({ ...tpl, html_footer: e.target.value })} className="font-mono text-xs" data-testid="email-tpl-footer" />
            </Field>

            <div className="flex gap-2 pt-3 border-t border-stone-200">
              <Button variant="secondary" onClick={reset} data-testid="email-tpl-reset">Reset ke Default</Button>
              <div className="flex-1" />
              <Button variant="secondary" onClick={() => renderPreview(subTab)}>Refresh Preview</Button>
              <Button onClick={save} disabled={saving} data-testid="email-tpl-save"><Plus className="h-4 w-4" />{saving ? "Menyimpan..." : "Simpan"}</Button>
            </div>
          </div>

          {/* Preview */}
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-stone-600 mb-2">Live Preview</div>
            <div className="border border-stone-300 rounded bg-white">
              <div className="bg-stone-50 border-b border-stone-200 px-3 py-2 text-xs text-stone-700">
                <strong>Subject:</strong> <span className="font-mono" data-testid="email-tpl-preview-subject">{previewSubject || "—"}</span>
              </div>
              <div className="p-4 overflow-auto max-h-[600px] text-sm" dangerouslySetInnerHTML={{ __html: previewHtml || "<i style='color:#a8a29e'>Belum ada preview.</i>" }} data-testid="email-tpl-preview-html" />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}



// Iter v34{ab} — Overdue Reminder settings (Daftar Tagihan email pengingat)
function OverdueReminderSettings() {
  const [cfg, setCfg] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [customEmail, setCustomEmail] = React.useState("");
  const [testing, setTesting] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // Iter v34{ae} — configurable send-time + preview/reset for overdue reminders.
  const [overdueTime, setOverdueTime] = React.useState("09:00");
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);

  React.useEffect(() => {
    api.get("/settings").then((r) => setCfg(r.data)).catch(() => toast.error("Gagal memuat pengaturan"));
    api.get("/users").then((r) => {
      const filt = (r.data || []).filter((u) => ["super_admin", "finance"].includes((u.role || "").toLowerCase()) && u.email);
      setUsers(filt);
    }).catch(() => {});
    api.get("/payment-reminder-settings").then((r) => {
      setOverdueTime(r.data?.overdue_reminder_time || "09:00");
    }).catch(() => {});
  }, []);

  if (!cfg) return <Card className="p-6">Memuat…</Card>;

  const update = (patch) => setCfg({ ...cfg, ...patch });
  const toggleAdminId = (id) => {
    const cur = cfg.reminder_admin_user_ids || [];
    update({ reminder_admin_user_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const addCustomEmail = () => {
    const e = (customEmail || "").trim();
    if (!e || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Format email tidak valid");
      return;
    }
    const cur = cfg.reminder_admin_custom_emails || [];
    if (cur.includes(e)) {
      toast.error("Email sudah ada di daftar");
      return;
    }
    update({ reminder_admin_custom_emails: [...cur, e] });
    setCustomEmail("");
  };
  const removeCustomEmail = (e) => {
    update({ reminder_admin_custom_emails: (cfg.reminder_admin_custom_emails || []).filter((x) => x !== e) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        reminder_days_threshold: Number(cfg.reminder_days_threshold) || 7,
        reminder_send_to_client: !!cfg.reminder_send_to_client,
        reminder_cc_admins: !!cfg.reminder_cc_admins,
        reminder_admin_user_ids: cfg.reminder_admin_user_ids || [],
        reminder_admin_custom_emails: cfg.reminder_admin_custom_emails || [],
        reminder_subject_template: cfg.reminder_subject_template || "",
        reminder_body_template: cfg.reminder_body_template || "",
      });
      // Iter v34{ae} — persist overdue send-time on payment-reminder-settings.
      try {
        const cur = await api.get("/payment-reminder-settings");
        await api.patch("/payment-reminder-settings", {
          ...cur.data,
          overdue_reminder_time: overdueTime || "09:00",
        });
      } catch (_e) { /* non-fatal */ }
      toast.success("Pengaturan tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.post("/email/reminder/test");
      toast.success(`Test email terkirim ke ${res.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim test");
    } finally {
      setTesting(false);
    }
  };
  const openPreview = async () => {
    setPreviewLoading(true);
    try {
      const r = await api.get("/payment-reminder-settings/overdue-preview");
      setPreviewData(r.data);
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat preview");
    } finally {
      setPreviewLoading(false);
    }
  };
  const resetTemplate = async () => {
    if (!window.confirm("Kembalikan Subject & Body ke template default? Perubahan tersimpan akan hilang.")) return;
    try {
      const r = await api.post("/payment-reminder-settings/overdue-reset-template");
      update({
        reminder_subject_template: r.data.default_subject || "",
        reminder_body_template: r.data.default_body || "",
      });
      toast.success("Template dikembalikan ke default.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal reset template");
    }
  };

  return (
    <div className="space-y-4" data-testid="overdue-reminder-settings">
      <Card className="p-6 space-y-5">
        <div>
          <h2 className="font-medium text-stone-800 mb-1">Email Reminder Tagihan Jatuh Tempo</h2>
          <p className="text-sm text-stone-600">Konfigurasi tombol &quot;Kirim Email Pengingat&quot; di pop-out PDF Daftar Tagihan, serta batch reminder.</p>
        </div>

        <Field label="Threshold Hari Mendekati Jatuh Tempo" hint="Tombol reminder aktif jika due_date dalam N hari mendatang atau sudah lewat.">
          <Input type="number" min={0} value={cfg.reminder_days_threshold ?? 7}
            onChange={(e) => update({ reminder_days_threshold: e.target.value })}
            data-testid="reminder-threshold-input" />
        </Field>

        {/* Iter v34{ae} — Configurable daily send-time for the overdue reminder cron job. */}
        <Field label="Jam Kirim Reminder Harian (WIB)" hint="Waktu APScheduler mengecek & mengirim email pengingat overdue (default 09:00).">
          <Input type="time" value={overdueTime}
            onChange={(e) => setOverdueTime(e.target.value)}
            className="!w-32"
            data-testid="overdue-reminder-time-input" />
        </Field>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="rm-send-client" checked={!!cfg.reminder_send_to_client}
            onChange={(e) => update({ reminder_send_to_client: e.target.checked })}
            data-testid="reminder-send-client-checkbox" />
          <label htmlFor="rm-send-client" className="text-sm">Kirim ke email klien (default)</label>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="rm-cc-admins" checked={!!cfg.reminder_cc_admins}
            onChange={(e) => update({ reminder_cc_admins: e.target.checked })}
            data-testid="reminder-cc-admins-checkbox" />
          <label htmlFor="rm-cc-admins" className="text-sm">CC ke admin terpilih</label>
        </div>

        {cfg.reminder_cc_admins && (
          <div className="space-y-3 pl-6 border-l-2 border-amber-200">
            <div>
              <label className="text-xs font-semibold text-stone-700 uppercase tracking-wider">Admin Penerima (dari User)</label>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto border border-stone-200 p-2 rounded-sm" data-testid="reminder-admin-user-list">
                {users.length === 0 && <p className="text-xs text-stone-400 italic">Tidak ada user dengan role super_admin/finance.</p>}
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 text-xs hover:bg-stone-50 px-2 py-1 cursor-pointer">
                    <input type="checkbox" checked={(cfg.reminder_admin_user_ids || []).includes(u.id)}
                      onChange={() => toggleAdminId(u.id)}
                      data-testid={`reminder-admin-user-${u.id}`} />
                    <span className="font-medium">{u.name || u.email}</span>
                    <span className="text-stone-400 font-mono">{u.email}</span>
                    <span className="text-[10px] bg-stone-100 text-stone-600 px-1.5 rounded">{u.role}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-700 uppercase tracking-wider">Email Custom (Free-form)</label>
              <div className="flex gap-2 mt-2">
                <Input value={customEmail} onChange={(e) => setCustomEmail(e.target.value)}
                  placeholder="user@example.com" type="email"
                  data-testid="reminder-custom-email-input" />
                <Button onClick={addCustomEmail} variant="ghost" data-testid="reminder-add-custom-email-btn">Tambah</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="reminder-custom-email-list">
                {(cfg.reminder_admin_custom_emails || []).map((e) => (
                  <span key={e} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 text-xs rounded-sm font-mono">
                    {e}
                    <button onClick={() => removeCustomEmail(e)} className="text-red-600 hover:text-red-800" data-testid={`reminder-remove-email-${e}`}>×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-medium text-stone-800 mb-1">Template Email</h3>
            <p className="text-xs text-stone-600">
              Placeholder yang tersedia:
              <code className="ml-1 bg-stone-100 px-1 text-[11px]">{`{invoice_number} {invoice_date} {due_date} {overdue_days} {balance} {client_name} {company_name}`}</code>
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={openPreview} disabled={previewLoading} data-testid="reminder-preview-btn">
              {previewLoading ? "Memuat…" : "Preview Email"}
            </Button>
            <Button variant="ghost" onClick={resetTemplate} data-testid="reminder-reset-template-btn">
              Reset ke Default
            </Button>
          </div>
        </div>
        <Field label="Subject">
          <Input value={cfg.reminder_subject_template || ""}
            onChange={(e) => update({ reminder_subject_template: e.target.value })}
            data-testid="reminder-subject-input" />
        </Field>
        <Field label="Body (HTML)">
          <Textarea rows={14} value={cfg.reminder_body_template || ""}
            onChange={(e) => update({ reminder_body_template: e.target.value })}
            className="font-mono text-xs"
            data-testid="reminder-body-input" />
        </Field>
      </Card>

      {previewOpen && previewData && (
        <Modal open onClose={() => setPreviewOpen(false)} title="Preview Email Reminder" size="lg">
          <div className="space-y-3" data-testid="reminder-preview-modal">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Subject</p>
              <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-sm font-medium" data-testid="reminder-preview-subject">
                {previewData.subject}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-1">Body (sample data)</p>
              <div
                className="bg-white border border-stone-200 rounded-sm p-4 max-h-[420px] overflow-y-auto text-sm"
                data-testid="reminder-preview-body"
                // Preview only — trusted server-rendered HTML from user's own template.
                dangerouslySetInnerHTML={{ __html: previewData.body_html }}
              />
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setPreviewOpen(false)} data-testid="reminder-preview-close">Tutup</Button>
            </div>
          </div>
        </Modal>
      )}

      <div className="flex justify-end gap-2 sticky bottom-0 bg-stone-50/80 backdrop-blur-sm p-3 -mx-2 rounded-sm border border-stone-200">
        <Button variant="ghost" onClick={sendTest} disabled={testing} data-testid="reminder-test-btn">
          {testing ? "Mengirim…" : "Kirim Test Email"}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="reminder-save-btn">
          {saving ? "Menyimpan…" : "Simpan Pengaturan"}
        </Button>
      </div>
    </div>
  );
}


// Iter v34bf — Master Kategori Pengajuan Fasilitas / Alat / Keperluan
function RequestCategoriesSettings() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get("/employee-requests/categories")
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Gagal memuat kategori"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const name = (form.name || "").trim();
    if (!name) return toast.error("Nama kategori wajib diisi");
    try {
      if (editingId) {
        await api.patch(`/employee-requests/categories/${editingId}`, { name, description: form.description || "" });
        toast.success("Kategori diperbarui");
      } else {
        await api.post("/employee-requests/categories", { name, description: form.description || "" });
        toast.success("Kategori ditambahkan");
      }
      setForm({ name: "", description: "" });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan kategori");
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name || "", description: r.description || "" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: "", description: "" });
  };

  const del = async (r) => {
    if (!window.confirm(`Hapus kategori "${r.name}"?`)) return;
    try {
      await api.delete(`/employee-requests/categories/${r.id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus");
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h2 className="font-display text-lg font-bold">Kategori Pengajuan Fasilitas / Alat / Keperluan</h2>
        <p className="text-xs text-stone-500 mt-1">Kelola daftar kategori yang dapat dipilih karyawan saat membuat pengajuan (misal: Alat Kerja, Fasilitas Kantor, ATK, Perlengkapan Keamanan, dsb).</p>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-end p-3 bg-stone-50 border border-stone-200">
        <Field label="Nama Kategori">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. Alat Kerja" data-testid="req-cat-name" />
        </Field>
        <Field label="Deskripsi (opsional)">
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Jelaskan cakupan kategori ini" />
        </Field>
        <div className="flex gap-2">
          <Button onClick={submit} data-testid="req-cat-save">
            {editingId ? <><Pencil className="h-4 w-4" /> Simpan</> : <><Plus className="h-4 w-4" /> Tambah</>}
          </Button>
          {editingId && <Button variant="ghost" onClick={cancelEdit}>Batal</Button>}
        </div>
      </div>

      {/* List */}
      <table className="w-full text-sm">
        <thead className="bg-stone-50">
          <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-2 px-3">Nama</th>
            <th className="py-2 px-3">Deskripsi</th>
            <th className="py-2 px-3 text-right w-24">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={3} className="py-6 text-center text-xs text-stone-400">Memuat…</td></tr>}
          {!loading && rows.length === 0 && <tr><td colSpan={3} className="py-6 text-center text-xs text-stone-400">Belum ada kategori. Tambahkan di form di atas.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className={`border-b border-stone-100 ${editingId === r.id ? "bg-blue-50/50" : ""}`}>
              <td className="py-2.5 px-3 font-medium">{r.name}</td>
              <td className="py-2.5 px-3 text-stone-500">{r.description || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                <div className="inline-flex gap-1">
                  <button onClick={() => startEdit(r)} className="text-stone-500 hover:text-blue-700 px-2" title="Edit" data-testid={`req-cat-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(r)} className="text-stone-500 hover:text-red-700 px-2" title="Hapus" data-testid={`req-cat-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}


// Iter v34bq — PO Reminder Settings (email penerima pengingat PO supply & billing).
function PoReminderSettings() {
  const [supplyEmails, setSupplyEmails] = useState([]);
  const [billingEmails, setBillingEmails] = useState([]);
  const [newSupply, setNewSupply] = useState("");
  const [newBilling, setNewBilling] = useState("");
  // Iter v34br (Task 3) — Template subject/intro + test email
  const [supplySubject, setSupplySubject] = useState("");
  const [supplyIntro, setSupplyIntro] = useState("");
  const [billingSubject, setBillingSubject] = useState("");
  const [billingIntro, setBillingIntro] = useState("");
  const [testSupplyEmail, setTestSupplyEmail] = useState("");
  const [testBillingEmail, setTestBillingEmail] = useState("");
  const [testing, setTesting] = useState(null); // "supply" | "billing" | null
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setSupplyEmails(Array.isArray(r.data.po_supply_reminder_emails) ? r.data.po_supply_reminder_emails : []);
        setBillingEmails(Array.isArray(r.data.po_billing_reminder_emails) ? r.data.po_billing_reminder_emails : []);
        setSupplySubject(r.data.po_supply_reminder_subject || "");
        setSupplyIntro(r.data.po_supply_reminder_intro || "");
        setBillingSubject(r.data.po_billing_reminder_subject || "");
        setBillingIntro(r.data.po_billing_reminder_intro || "");
      })
      .finally(() => setLoading(false));
  }, []);
  const emailValid = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((e || "").trim());
  const addEmail = (list, setList, val, setVal) => {
    const e = (val || "").trim();
    if (!emailValid(e)) { toast.error("Format email tidak valid"); return; }
    if (list.includes(e)) { toast.error("Email sudah ada di daftar"); return; }
    setList([...list, e]);
    setVal("");
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        po_supply_reminder_emails: supplyEmails,
        po_billing_reminder_emails: billingEmails,
        po_supply_reminder_subject: supplySubject,
        po_supply_reminder_intro: supplyIntro,
        po_billing_reminder_subject: billingSubject,
        po_billing_reminder_intro: billingIntro,
      });
      toast.success("Pengaturan reminder PO tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  const sendTest = async (kind) => {
    const email = kind === "supply" ? testSupplyEmail : testBillingEmail;
    if (!emailValid(email)) { toast.error("Isi email test yang valid dulu"); return; }
    // Save current settings terlebih dahulu supaya template tercapture di test.
    setTesting(kind);
    try {
      // Auto-save dulu supaya template konsisten dgn preview
      await api.patch("/settings", {
        po_supply_reminder_subject: supplySubject,
        po_supply_reminder_intro: supplyIntro,
        po_billing_reminder_subject: billingSubject,
        po_billing_reminder_intro: billingIntro,
      }).catch(() => {});
      const r = await api.post(`/pengumuman/reminders/po-${kind}/test`, { email });
      if (r.data?.ok) {
        toast.success(`Test email terkirim ke ${email} (${r.data.po_count} PO pending)`);
      } else {
        toast.error("Gagal kirim test email — cek log server");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim test");
    } finally { setTesting(null); }
  };
  if (loading) return <Card className="p-6 text-stone-400 text-sm">Memuat...</Card>;

  const renderSection = (title, emails, setEmails, newE, setNewE, subject, setSubject, intro, setIntro, testE, setTestE, kind, tone) => (
    <div className={`border rounded-lg p-4 space-y-3 ${tone === "amber" ? "border-amber-200 bg-amber-50/40" : "border-red-200 bg-red-50/40"}`}>
      <div className="flex items-center gap-2">
        <span className={`text-xs font-semibold uppercase tracking-widest ${tone === "amber" ? "text-amber-800" : "text-red-800"}`}>{title}</span>
        <span className="text-[10px] text-stone-500">({emails.length} email penerima)</span>
      </div>
      <Field label="Daftar Email Penerima" hint="Tekan Enter atau klik Tambah setelah mengetik.">
        <div className="flex gap-2">
          <Input type="email" value={newE} onChange={(e) => setNewE(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(emails, setEmails, newE, setNewE); } }}
            placeholder="mis: penerima@perusahaan.id" data-testid={`po-${kind}-email-input`}/>
          <Button variant="secondary" onClick={() => addEmail(emails, setEmails, newE, setNewE)} data-testid={`po-${kind}-email-add`}>
            <Plus className="h-3.5 w-3.5" /> Tambah
          </Button>
        </div>
      </Field>
      <div className="flex flex-wrap gap-2" data-testid={`po-${kind}-email-list`}>
        {emails.map((e) => (
          <span key={e} className={`inline-flex items-center gap-1 px-2 py-1 bg-white border text-xs rounded-sm font-mono ${tone === "amber" ? "border-amber-200" : "border-red-200"}`}>
            {e}
            <button onClick={() => setEmails(emails.filter((x) => x !== e))} className="text-red-600 hover:text-red-800 ml-1" data-testid={`po-${kind}-email-remove-${e}`}>×</button>
          </span>
        ))}
        {emails.length === 0 && <span className="text-xs text-stone-400 italic">Belum ada email penerima.</span>}
      </div>
      {/* Iter v34br (Task 3) — Template subject + intro */}
      <Field label="Subject Email" hint="Gunakan {po_count} untuk placeholder jumlah PO.">
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
          placeholder={kind === "supply" ? "Pengingat: {po_count} PO Belum Di-supply" : "Pengingat: {po_count} PO Belum Ditagih"}
          data-testid={`po-${kind}-subject`}/>
      </Field>
      <Field label="Intro Body Email (HTML)" hint="Diletakkan sebelum tabel PO. Kosongkan untuk pakai default.">
        <textarea value={intro} onChange={(e) => setIntro(e.target.value)}
          rows={3}
          placeholder="mis: Berikut daftar PO yang masih menunggu tindak lanjut Anda:"
          className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y"
          data-testid={`po-${kind}-intro`}/>
      </Field>
      {/* Iter v34br (Task 3) — Kirim Test Email */}
      <Field label="Kirim Test Email ke" hint="Preview isi email tanpa mengganggu daftar penerima resmi. Test email TIDAK disimpan ke riwayat.">
        <div className="flex gap-2">
          <Input type="email" value={testE} onChange={(e) => setTestE(e.target.value)} placeholder="email@anda.id" data-testid={`po-${kind}-test-email-input`}/>
          <Button variant="secondary" onClick={() => sendTest(kind)} disabled={testing === kind} data-testid={`po-${kind}-test-send`}>
            <Send className="h-3.5 w-3.5"/> {testing === kind ? "Mengirim..." : "Kirim Test"}
          </Button>
        </div>
      </Field>
    </div>
  );

  return (
    <Card className="p-6 space-y-6" data-testid="po-reminder-settings">
      <div>
        <h3 className="text-lg font-semibold text-stone-800">Email Reminder PO</h3>
        <p className="text-xs text-stone-500 mt-1">
          Kelola daftar penerima + template email untuk pengingat PO. Email dikirim manual dari halaman <b>Pengumuman</b>.
          Pengirim: <b>no-reply</b>.
        </p>
      </div>
      {renderSection("PO Belum Di-supply", supplyEmails, setSupplyEmails, newSupply, setNewSupply, supplySubject, setSupplySubject, supplyIntro, setSupplyIntro, testSupplyEmail, setTestSupplyEmail, "supply", "amber")}
      {renderSection("PO Belum Ditagih", billingEmails, setBillingEmails, newBilling, setNewBilling, billingSubject, setBillingSubject, billingIntro, setBillingIntro, testBillingEmail, setTestBillingEmail, "billing", "red")}
      <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="po-reminder-save">
          {saving ? "Menyimpan..." : "Simpan Semua Pengaturan"}
        </Button>
      </div>
    </Card>
  );
}

// Iter v34br (Task 4) — Template Pengumuman untuk pemakaian berulang.
function AnnouncementTemplateSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings")
      .then((r) => setTemplates(Array.isArray(r.data.announcement_templates) ? r.data.announcement_templates : []))
      .finally(() => setLoading(false));
  }, []);
  const addTemplate = () => setTemplates([...templates, { name: "", subject: "", body_html: "" }]);
  const removeTemplate = (idx) => setTemplates(templates.filter((_, i) => i !== idx));
  const updateTemplate = (idx, k, v) => {
    const copy = [...templates];
    copy[idx] = { ...copy[idx], [k]: v };
    setTemplates(copy);
  };
  const save = async () => {
    // Validasi: nama wajib.
    for (const t of templates) {
      if (!t.name?.trim()) { toast.error("Setiap template harus punya nama"); return; }
    }
    setSaving(true);
    try {
      await api.patch("/settings", { announcement_templates: templates });
      toast.success(`${templates.length} template pengumuman tersimpan`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally { setSaving(false); }
  };
  if (loading) return <Card className="p-6 text-stone-400 text-sm">Memuat...</Card>;
  return (
    <Card className="p-6 space-y-4" data-testid="announcement-template-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-stone-800">Template Pengumuman</h3>
          <p className="text-xs text-stone-500 mt-1">
            Buat template pengumuman siap-pakai (mis. Libur Nasional, Perubahan Harga, Info Umum). Template ini akan muncul sebagai
            dropdown "Pilih Template" di halaman <b>Pengumuman</b> untuk auto-fill Subject + Isi.
          </p>
        </div>
        <Button variant="secondary" onClick={addTemplate} data-testid="anno-template-add">
          <Plus className="h-3.5 w-3.5" /> Template Baru
        </Button>
      </div>
      {templates.length === 0 ? (
        <p className="text-center text-sm text-stone-400 py-8 italic">Belum ada template. Klik "Template Baru" untuk mulai.</p>
      ) : (
        <div className="space-y-4">
          {templates.map((t, idx) => (
            <div key={idx} className="border border-stone-200 rounded-lg p-4 space-y-3 bg-stone-50/30" data-testid={`anno-template-row-${idx}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Template #{idx + 1}</span>
                <button onClick={() => removeTemplate(idx)} className="text-red-600 hover:text-red-800 text-xs" data-testid={`anno-template-remove-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5"/>
                </button>
              </div>
              <Field label="Nama Template" required>
                <Input value={t.name || ""} onChange={(e) => updateTemplate(idx, "name", e.target.value)}
                  placeholder="mis: Libur Nasional, Perubahan Harga Semen" data-testid={`anno-template-name-${idx}`}/>
              </Field>
              <Field label="Subject Email">
                <Input value={t.subject || ""} onChange={(e) => updateTemplate(idx, "subject", e.target.value)}
                  placeholder="mis: [INFO] Libur Nasional Idul Fitri 1447 H" data-testid={`anno-template-subject-${idx}`}/>
              </Field>
              <Field label="Isi Body (HTML)">
                <textarea value={t.body_html || ""} onChange={(e) => updateTemplate(idx, "body_html", e.target.value)}
                  rows={6}
                  placeholder="<p>Kepada Yth Pelanggan,</p><p>Bersama ini kami sampaikan...</p>"
                  className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y font-mono-num"
                  data-testid={`anno-template-body-${idx}`}/>
              </Field>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end pt-3 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="anno-template-save">
          {saving ? "Menyimpan..." : `Simpan ${templates.length} Template`}
        </Button>
      </div>
    </Card>
  );
}


// =============================================================================
// Iter v34bw (#1) — Validasi Pendaftaran Karyawan (KTP clarity + blocked banks)
// =============================================================================
function EmployeeRegistrationSettings() {
  const [threshold, setThreshold] = useState(100);
  const [keywords, setKeywords] = useState(["dana"]);
  const [newKw, setNewKw] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => {
        setThreshold(Number(r.data?.ktp_clarity_threshold ?? 100));
        setKeywords(Array.isArray(r.data?.blocked_bank_keywords) ? r.data.blocked_bank_keywords : ["dana"]);
      })
      .catch(() => toast.error("Gagal memuat pengaturan"))
      .finally(() => setLoading(false));
  }, []);

  const addKw = () => {
    const v = newKw.trim().toLowerCase();
    if (!v) return;
    if (keywords.map((k) => k.toLowerCase()).includes(v)) { setNewKw(""); return; }
    setKeywords([...keywords, v]);
    setNewKw("");
  };
  const removeKw = (i) => setKeywords(keywords.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        ktp_clarity_threshold: Number(threshold),
        blocked_bank_keywords: keywords.map((k) => String(k).trim()).filter(Boolean),
      });
      toast.success("Pengaturan pendaftaran karyawan disimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan pengaturan");
    } finally { setSaving(false); }
  };

  if (loading) return <Card><p className="text-sm text-stone-500 text-center py-8">Memuat...</p></Card>;

  return (
    <Card className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-stone-900 mb-1">Validasi Pendaftaran Karyawan</h3>
        <p className="text-xs text-stone-500">Atur syarat kejelasan foto KTP dan daftar kata terlarang pada nama bank saat karyawan melengkapi profil.</p>
      </div>

      <Field label="Ambang Kejelasan Foto KTP (Laplacian Variance)">
        <div className="flex items-center gap-3">
          <Input
            type="number"
            min={10}
            max={500}
            step={5}
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            className="max-w-[140px]"
            data-testid="ktp-clarity-threshold-input"
          />
          <div className="text-[11px] text-stone-500 leading-4">
            <div>Rekomendasi: <b>50</b> permisif · <b>100</b> default · <b>200</b> ketat.</div>
            <div>Skor di bawah nilai ini akan ditolak (foto buram/kurang cahaya).</div>
          </div>
        </div>
      </Field>

      <Field label="Kata Terlarang pada Nama Bank">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              placeholder="contoh: dana, ovo, gopay"
              value={newKw}
              onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKw(); } }}
              data-testid="blocked-kw-input"
            />
            <Button variant="outline" onClick={addKw} data-testid="blocked-kw-add">Tambah</Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {keywords.length === 0 && <span className="text-xs text-stone-400 italic">Tidak ada kata terlarang.</span>}
            {keywords.map((k, i) => (
              <Badge key={i} tone="terra" className="flex items-center gap-1" data-testid={`blocked-kw-${i}`}>
                {k}
                <button onClick={() => removeKw(i)} className="text-red-700 hover:text-red-900 ml-1" title="Hapus" data-testid={`blocked-kw-remove-${i}`}>×</button>
              </Badge>
            ))}
          </div>
          <p className="text-[11px] text-stone-500">Substring match, case-insensitive. Nama bank yang mengandung kata ini akan ditolak saat karyawan submit.</p>
        </div>
      </Field>

      <div className="flex justify-end pt-3 border-t border-stone-200">
        <Button onClick={save} disabled={saving} data-testid="employee-reg-save">
          {saving ? "Menyimpan..." : "Simpan Pengaturan"}
        </Button>
      </div>
    </Card>
  );
}


// Iter v34ci — PIN Kiosk Absen (6 digit) untuk keluar fullscreen / switch app.
function KioskPinSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  // Iter v34cl — Face threshold slider
  const [threshold, setThreshold] = useState(0.42);
  const [savingTh, setSavingTh] = useState(false);

  React.useEffect(() => {
    api.get("/settings").then((r) => {
      setPinSet(!!r.data?.kiosk_pin_set);
      const t = parseFloat(r.data?.kiosk_face_threshold);
      if (!isNaN(t) && t >= 0.20 && t <= 0.60) setThreshold(t);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!/^\d{6}$/.test(pin)) return toast.error("PIN harus 6 digit angka");
    if (pin !== pin2) return toast.error("Konfirmasi PIN tidak cocok");
    setSaving(true);
    try {
      await api.patch("/settings", { kiosk_pin: pin });
      toast.success("PIN Kiosk tersimpan");
      setPin(""); setPin2("");
      setPinSet(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };

  const clearPin = async () => {
    if (!window.confirm("Hapus PIN Kiosk? Halaman kiosk tidak akan bisa dikunci lagi.")) return;
    setSaving(true);
    try {
      await api.patch("/settings", { kiosk_pin: "" });
      toast.success("PIN Kiosk dihapus");
      setPinSet(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal hapus"); }
    finally { setSaving(false); }
  };

  const saveThreshold = async () => {
    setSavingTh(true);
    try {
      await api.patch("/settings", { kiosk_face_threshold: threshold });
      toast.success(`Threshold wajah tersimpan (${threshold.toFixed(2)})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSavingTh(false); }
  };

  if (loading) return <Card className="p-6"><Skeleton className="h-32"/></Card>;

  return (
    <Card className="p-6 space-y-6" data-testid="kiosk-pin-settings">
      {/* PIN Section */}
      <div>
        <h3 className="font-display text-lg font-semibold text-stone-900 mb-1">PIN Kiosk Absen (Face)</h3>
        <p className="text-xs text-stone-600">PIN 6 digit dibutuhkan untuk keluar fullscreen atau membuka kembali halaman kiosk yang terkunci. Simpan PIN ini di tempat aman dan bagikan hanya ke supervisor.</p>
      </div>
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-sm">
        Status: {pinSet ? <span className="font-semibold text-emerald-700">PIN sudah diatur ✓</span> : <span className="font-semibold text-amber-700">PIN belum diatur</span>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1">PIN Baru (6 digit)</label>
          <Input type="password" inputMode="numeric" pattern="\d{6}" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0,6))} placeholder="••••••" data-testid="kiosk-pin-input"/>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1">Konfirmasi PIN</label>
          <Input type="password" inputMode="numeric" pattern="\d{6}" maxLength={6} value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0,6))} placeholder="••••••" data-testid="kiosk-pin-confirm"/>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving || pin.length !== 6 || pin !== pin2} data-testid="kiosk-pin-save">{saving ? "Menyimpan…" : "Simpan PIN"}</Button>
        {pinSet && (
          <Button variant="outline" onClick={clearPin} disabled={saving} className="!text-red-700 !border-red-300 hover:!bg-red-50" data-testid="kiosk-pin-clear">Hapus PIN</Button>
        )}
      </div>

      {/* Face Threshold Section */}
      <div className="pt-6 border-t border-stone-200">
        <h3 className="font-display text-lg font-semibold text-stone-900 mb-1">Akurasi Pengenalan Wajah (Kiosk)</h3>
        <p className="text-xs text-stone-600 mb-3">Semakin rendah nilai, semakin ketat kecocokan wajah — mengurangi salah identifikasi antar karyawan. Rekomendasi: <b>0.38–0.42</b> untuk plant. Tampilkan pesan "Wajah tidak dikenali" bila jarak melebihi ambang ini.</p>
        <div className="space-y-2 max-w-lg" data-testid="kiosk-face-threshold-wrap">
          <div className="flex items-center justify-between text-xs">
            <span className="font-mono text-emerald-700">Sangat Ketat 0.30</span>
            <span className="font-mono font-semibold text-stone-900 text-lg" data-testid="kiosk-face-threshold-value">{threshold.toFixed(2)}</span>
            <span className="font-mono text-amber-700">Longgar 0.55</span>
          </div>
          <input
            type="range" min="0.30" max="0.55" step="0.01" value={threshold}
            onChange={(e) => setThreshold(parseFloat(e.target.value))}
            className="w-full accent-terra-500"
            data-testid="kiosk-face-threshold-slider"
          />
          <div className="flex gap-2 pt-2">
            <Button onClick={saveThreshold} disabled={savingTh} data-testid="kiosk-face-threshold-save">
              {savingTh ? "Menyimpan…" : "Simpan Threshold"}
            </Button>
            <Button variant="outline" onClick={() => setThreshold(0.42)} disabled={savingTh}>Reset Default (0.42)</Button>
          </div>
        </div>
      </div>
    </Card>
  );
}


